import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DeleteTwoTone, EyeTwoTone, ProfileTwoTone, StarFilled } from "@ant-design/icons";
import SubHeader from "../../components/SubHeader/SubHeader";
import CustomTable from "../../components/CustomTable/CustomTable";
import SelectInput from "../../components/SelectInput/SelectInput";
import Loader from "../../components/Loader/Loader";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import { GET_ALL_STUDENTS, DELETE_STUDENT_BY_ID, GET_ALL_BRANCH, GET_ASSESSMENTTYPE_SCHEDULE } from "../../utils/apiPath";
import { postApi, deleteApi, getApi } from "../../utils/apiService";
import { errorToast, successToast } from "../../services/ToastHelper";
import { formatDateDDMMYYYY } from "../../services/dateFormatter";
import { getPermissionForModule } from "../../services/permissions";
import "./Students.scss";

const SECTION_OPTIONS = ["A", "B", "C", "D", "E", "F", "G", "H"].map((s) => ({
    label: `Section ${s}`,
    value: s,
}));

const Students = () => {
    const { id, gradeId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const role = sessionStorage.getItem("role");
    const isAdmin = role === "Admin";

    const { assessmentTypeName, gradeName } = location.state || {};
    const currentSubTitle = [assessmentTypeName, gradeName].filter(Boolean).join(" > ");

    const [isLoading, setIsLoading] = useState(false);
    const [studentsData, setStudentsData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchString] = useState("");
    const [branchName, setBranchName] = useState(
        isAdmin ? null : sessionStorage.getItem("branchName") || null
    );
    const [branchOptions, setBranchOptions] = useState([]);
    const [sectionName, setSectionName] = useState(null);
    const [scheduleOptions, setScheduleOptions] = useState([]);
    const [scheduleId, setScheduleId] = useState(null);

    const studentsPermission = getPermissionForModule("Student Assessments");

    const fetchBranches = async () => {
        const { statusCode, data } = await getApi(GET_ALL_BRANCH);
        if (statusCode === 200) {
            const list = Array.isArray(data) ? data : data?.data || [];
            setBranchOptions(
                list.map((b) => ({ label: b.name, value: b.name }))
            );
        }
    };

    useEffect(() => {
        if (isAdmin) fetchBranches();
    }, [isAdmin]);

    const fetchSchedules = async () => {
        const { statusCode, data } = await getApi(GET_ASSESSMENTTYPE_SCHEDULE, {
            params: { assessmentTypeId: id, attendedOnly: true },
        });
        if (statusCode === 200) {
            const list = Array.isArray(data) ? data : data?.data || [];
            setScheduleOptions(
                list.map((s) => ({
                    label: [`${s.scheduledDate} ${s.scheduledTime}`, s.academicYear].filter(Boolean).join(" · "),
                    value: s.id,
                }))
            );
        }
    };

    useEffect(() => {
        fetchSchedules();
    }, [id]);

    const fetchAllStudents = async (pIndex = pageIndex, pSize = pageSize) => {
        setIsLoading(true);
        const payload = {
            pageIndex: pIndex,
            pageSize: pSize,
            searchString: searchString,
            assessmentTypeId: id,
            gradeId: gradeId,
            ...(branchName && { branchName }),
            ...(sectionName && { sectionName }),
            assessmentTypeScheduleId: scheduleId ?? null,
        };
        const { statusCode, data, message, totalRecords } = await postApi(
            GET_ALL_STUDENTS,
            payload
        );
        if (statusCode === 200) {
            setStudentsData(data || []);
            setTotalRecords(totalRecords || 0);
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchAllStudents();
    }, [pageIndex, pageSize]);

    useEffect(() => {
        if (pageIndex === 0) {
            fetchAllStudents(0, pageSize);
        } else {
            setPageIndex(0);
        }
    }, [branchName, sectionName, scheduleId]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleDelete = async (record) => {
        setIsLoading(true);
        try {
            const { statusCode, message } = await deleteApi(DELETE_STUDENT_BY_ID, {
                params: { id: record?.assessmentId },
            });

            if (statusCode === 200) {
                successToast(message);
                await fetchAllStudents();
            } else {
                errorToast(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = (url) => {
        if (url) {
            window.open(url, "_blank");
        } else {
            errorToast("No report available for download");
        }
    };

    const handleViewAssessment = (record) => {
        navigate(`/student-assessment-result/${record.assessmentId}`, {
            state: {
                ...location.state,
                studentName: record.studentName,
                assessmentId: record.assessmentId
            }
        });
    };

    const columns = [
        {
            title: "Student Name",
            dataIndex: "studentName",
            key: "studentName",
        },
        {
            title: "Class Name",
            dataIndex: "className",
            key: "className",
        },
         {
            title: "Section",
            dataIndex: "sectionName",
            key: "sectionName",
        },
        {
            title: "Academic Year",
            dataIndex: "academicYear",
            key: "academicYear",
        },
        {
            title: "Created Date",
            dataIndex: "createdDate",
            key: "createdDate",
            render: (text) => formatDateDDMMYYYY(text),
        },
        {
            title: "Overall Rating",
            dataIndex: "overallAverage",
            key: "overallAverage",
            render: (val) => {
                if (val == null) return "-";
                const color = val >= 4 ? "#10b981" : val >= 2.5 ? "#f59e0b" : "#ef4444";
                return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600, color }}>
                        <StarFilled style={{ fontSize: 13 }} />
                        {val} <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 12 }}>/ 5</span>
                    </span>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <ActionGroup>
                    {studentsPermission?.canView && (
                        <>
                            <ActionIcon
                                title="View Report"
                                icon={<EyeTwoTone twoToneColor={!record?.studentReport ? ["#bfbfbf", "#f5f5f5"] : ["#0e82fd", "#e6f4ff"]} />}
                                onClick={() => handleDownload(record?.studentReport)}
                                disabled={!record?.studentReport}
                            />
                            <ActionIcon
                                title="Assessment Result"
                                icon={<ProfileTwoTone twoToneColor={!record?.studentReport ? ["#bfbfbf", "#f5f5f5"] : ["#13c2c2", "#e6fffb"]} />}
                                onClick={() => handleViewAssessment(record)}
                                disabled={!record?.studentReport}
                            />
                        </>
                    )}
                    {studentsPermission?.canDelete && (
                        <ActionIcon
                            title="Delete Student"
                            icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                            confirm={{
                                enabled: true,
                                title: `Are you sure you want to delete ${record?.studentName}'s assessment?`,
                                okText: "Delete",
                                cancelText: "Cancel",
                                onConfirm: () => handleDelete(record),
                            }}
                        />
                    )}
                </ActionGroup>
            ),
        },
    ];

    return (
        <div className="assessment">
            {isLoading && <Loader />}

            <SubHeader
                title="Students"
                subTitle={currentSubTitle}
                showBack={true}
                onBack={handleBack}
                rightExtra={
                    <div style={{ display: "flex", gap: "12px" }}>
                        {isAdmin && (
                            <SelectInput
                                placeholder="Filter by Branch"
                                options={branchOptions}
                                value={branchName}
                                onChange={(val) => setBranchName(val ?? null)}
                                allowClear
                                size="middle"
                            />
                        )}
                        <SelectInput
                            placeholder="Filter by Section"
                            options={SECTION_OPTIONS}
                            value={sectionName}
                            onChange={(val) => setSectionName(val ?? null)}
                            allowClear
                            size="middle"
                        />
                        <SelectInput
                            placeholder="Filter by Schedule"
                            options={scheduleOptions}
                            value={scheduleId}
                            onChange={(val) => setScheduleId(val ?? null)}
                            allowClear
                            size="middle"
                        />
                    </div>
                }
            />

            <CustomTable
                columns={columns}
                dataSource={studentsData}
                loading={isLoading}
                pageIndex={pageIndex}
                pageSize={pageSize}
                total={totalRecords}
                onPageChange={(current, size) => {
                    setPageIndex(current - 1);
                    setPageSize(size);
                }}
                rowKey="assessmentId"
            />
        </div>
    );
};

export default Students;
